// ===== EMR/EHR MESSAGE TYPE CONSTANTS =====

// Maps to tblEhr_MessageType PKey values (see EHRUtils.vb EhrMessageType enum)
export const EMR_MESSAGE_TYPE = {
  ADMISSION: 1,            // A01
  TRANSFER: 2,             // A02
  DISCHARGE: 3,            // A03
  OUTPATIENT_ADMISSION: 4, // A04
  PREADMISSION: 5,         // A05
  UPDATE: 6,               // A08
  CANCEL_ADMISSION: 7,     // A11
  CANCEL_TRANSFER: 8,      // A12
  CANCEL_DISCHARGE: 9,     // A13
  LEAVE_OF_ABSENCE: 10,    // A21
  RETURN_FROM_LOA: 11,     // A22
  CANCEL_PREADMISSION: 12, // A27
  GENERAL_ORDER: 13,       // ORM
} as const;
export type EmrMessageTypeId = (typeof EMR_MESSAGE_TYPE)[keyof typeof EMR_MESSAGE_TYPE];

/**
 * Message types that matched nobody can be matched to a resident by hand: update,
 * discharge and transfer (WinForms opens Resident Match for these:
 * frmDietech_Ehr_Pending_DataGrid.vb:199-202).
 */
export const EHR_MATCHABLE_MESSAGE_TYPES: ReadonlySet<number> = new Set<number>([
  EMR_MESSAGE_TYPE.TRANSFER,
  EMR_MESSAGE_TYPE.DISCHARGE,
  EMR_MESSAGE_TYPE.UPDATE,
]);

export const EMR_MESSAGE_TYPE_CONFIG: Record<
  number,
  { label: string; color: string; bgColor: string; icon: string; iconClass: string }
> = {
  [EMR_MESSAGE_TYPE.ADMISSION]:            { label: 'Admission',      color: '#fff', bgColor: '#3b82f6', icon: 'bi-box-arrow-in-right',    iconClass: 'admission' },
  [EMR_MESSAGE_TYPE.TRANSFER]:             { label: 'Transfer',       color: '#fff', bgColor: '#10b981', icon: 'bi-arrow-left-right',      iconClass: 'transfer' },
  [EMR_MESSAGE_TYPE.DISCHARGE]:            { label: 'Discharge',      color: '#fff', bgColor: '#f59e0b', icon: 'bi-box-arrow-right',       iconClass: 'discharge' },
  [EMR_MESSAGE_TYPE.OUTPATIENT_ADMISSION]: { label: 'Outpatient',     color: '#fff', bgColor: '#8b5cf6', icon: 'bi-box-arrow-in-right',    iconClass: 'outpatient' },
  [EMR_MESSAGE_TYPE.PREADMISSION]:         { label: 'Pre-Admit',      color: '#fff', bgColor: '#64748b', icon: 'bi-clock-history',         iconClass: 'preadmission' },
  [EMR_MESSAGE_TYPE.UPDATE]:               { label: 'Update',         color: '#fff', bgColor: '#6b7280', icon: 'bi-arrow-repeat',          iconClass: 'update' },
  [EMR_MESSAGE_TYPE.CANCEL_ADMISSION]:     { label: 'Cancel Admit',   color: '#fff', bgColor: '#ef4444', icon: 'bi-x-circle',             iconClass: 'cancel' },
  [EMR_MESSAGE_TYPE.CANCEL_TRANSFER]:      { label: 'Cancel Xfer',    color: '#fff', bgColor: '#ef4444', icon: 'bi-x-circle',             iconClass: 'cancel' },
  [EMR_MESSAGE_TYPE.CANCEL_DISCHARGE]:     { label: 'Cancel DC',      color: '#fff', bgColor: '#ef4444', icon: 'bi-x-circle',             iconClass: 'cancel' },
  [EMR_MESSAGE_TYPE.LEAVE_OF_ABSENCE]:     { label: 'LOA',            color: '#fff', bgColor: '#a855f7', icon: 'bi-door-open',            iconClass: 'loa' },
  [EMR_MESSAGE_TYPE.RETURN_FROM_LOA]:      { label: 'Return LOA',     color: '#fff', bgColor: '#22c55e', icon: 'bi-door-closed',          iconClass: 'return-loa' },
  [EMR_MESSAGE_TYPE.CANCEL_PREADMISSION]:  { label: 'Cancel Pre-Admit', color: '#fff', bgColor: '#ef4444', icon: 'bi-x-circle',          iconClass: 'cancel' },
  [EMR_MESSAGE_TYPE.GENERAL_ORDER]:        { label: 'Order',          color: '#fff', bgColor: '#06b6d4', icon: 'bi-clipboard2-pulse',     iconClass: 'order' },
};

export function getMessageTypeConfig(typeId: number | null | undefined) {
  return EMR_MESSAGE_TYPE_CONFIG[typeId ?? 0] ?? {
    label: 'Unknown', color: '#fff', bgColor: '#374151', icon: 'bi-envelope', iconClass: 'unknown',
  };
}

// ===== PENDING MESSAGE STATUS =====

export const PENDING_STATUS = {
  READY: 'ready',
  NO_MATCH: 'no-match',
  MAPPING_ERROR: 'mapping-error',
  WAITING: 'waiting',
} as const;
export type PendingStatus = (typeof PENDING_STATUS)[keyof typeof PENDING_STATUS];

export const PENDING_STATUS_CONFIG: Record<
  PendingStatus,
  { label: string; color: string; bgColor: string; icon: string }
> = {
  [PENDING_STATUS.READY]:         { label: 'Ready',         color: '#166534', bgColor: '#dcfce7', icon: 'bi-check-circle-fill' },
  [PENDING_STATUS.NO_MATCH]:      { label: 'No Match',      color: '#92400e', bgColor: '#fef3c7', icon: 'bi-question-circle' },
  [PENDING_STATUS.MAPPING_ERROR]: { label: 'Error',         color: '#991b1b', bgColor: '#fee2e2', icon: 'bi-exclamation-triangle-fill' },
  [PENDING_STATUS.WAITING]:       { label: 'Waiting',       color: '#1e40af', bgColor: '#dbeafe', icon: 'bi-hourglass-split' },
};

export const HISTORY_STATUS = {
  SUCCESS: 'success',
  ERROR: 'error',
} as const;
export type HistoryStatus = (typeof HISTORY_STATUS)[keyof typeof HISTORY_STATUS];

export const HISTORY_STATUS_CONFIG: Record<
  HistoryStatus,
  { label: string; color: string; bgColor: string; icon: string }
> = {
  [HISTORY_STATUS.SUCCESS]: { label: 'Success', color: '#166534', bgColor: '#dcfce7', icon: 'bi-check-circle-fill' },
  [HISTORY_STATUS.ERROR]:   { label: 'Error',   color: '#991b1b', bgColor: '#fee2e2', icon: 'bi-exclamation-triangle-fill' },
};

// ===== CORE DATA MODELS =====

/** Maps to C# TblPatient_Pending - System.Text.Json camelCase serialization */
export interface TblPatientPending {
  pKey_Patient_Pending: number;
  dateEntered: string;
  patientID: string | null;
  firstName: string | null;
  lastName: string | null;
  middleInitial: string | null;
  title: string | null;
  dateOfBirth: string | null;
  sex: string | null;
  preferredName: string | null;
  race: string | null;
  religion: string | null;
  floor: string | null;
  wing: string | null;
  unit: string | null;
  room: string | null;
  bed: string | null;
  doctorFirstName: string | null;
  doctorLastName: string | null;
  admitSource: string | null;
  dischargedLocation: string | null;
  admitDateTime: string | null;
  dischargedDatetime: string | null;
  dischargeReason: string | null;
  dateProcessed: string | null;
  fKey_Ehr_MessageType: number | null;
  dateEhrDownload: string;
  toBeDeleted: boolean;
  pmSec_User_FirstName: string | null;
  pmSec_User_LastName: string | null;
  pmSec_User_UserName: string | null;
  processError: boolean;
  errorText: string | null;
  fKey_Facility: number;
  noInfoToUpdate: boolean;
  supported: boolean;
  obx: string | null;
  fKey_User_Lock: number | null;
  lockTimeStamp: string | null;
  dietOrderIsCombined: boolean;
  allergyIsCombined: boolean;
  dietOrderCombined: string | null;
  allergyCombined: string | null;
  image: string | null; // base64 byte[]
  pmSec_User_FirstName_Locked: string | null;
  pmSec_User_LastName_Locked: string | null;
  heightIn: number | null;
  linked_ID: string | null;
  dietType_Clear: boolean;
  allergy_Clear: boolean;
  isOnLeave: boolean;
  doNotProcess: boolean;
  probableExistingPatient: boolean;
  outPatient: boolean;
  hospitalService: string | null;
  visitNumber: string | null;
  patientClass: string | null;
  waitingForAdmitSource: boolean;
  hasMappingError: boolean;
  matchFound: boolean;
  /** Number of associated tblPatient_Pending_DietOrder rows.
   *  Populated only by the GetHistory SQL (shared
   *  SQL_tblPatient_Pending_Select_Processed) — drives the
   *  "Contains Diet" column on the EHR tab (true when > 0). */
  dietCount: number;
}

/**
 * The fields of a Message History list row: C# `EhrHistoryListRow`, from
 * GET api/EMR/GetHistory/{facility}. Only what the list reads (its columns, Find
 * search, status icon / error tint, the "{Resident} History" filter) and what the
 * read-only message viewer and its dialogs read off the row. The viewer's text
 * comes from api/EMR/MessageContents.
 *
 * The list used to carry the whole ~60-field message: two weeks at Kansas Heart was
 * a 10 MB download. Add a field here only together with the C# DTO and its SQL
 * (SQL_tblPatient_Pending_Select_ProcessedList); EhrHistoryListRowTests checks the
 * two lists match.
 */
export const EHR_HISTORY_ROW_FIELDS = [
  'pKey_Patient_Pending',
  'dateEntered',
  'dateProcessed',
  'fKey_Ehr_MessageType',
  'patientID',
  'linked_ID',
  'visitNumber',
  'title',
  'firstName',
  'middleInitial',
  'lastName',
  'dateOfBirth',
  'sex',
  'floor',
  'wing',
  'unit',
  'room',
  'bed',
  'pmSec_User_UserName',
  'processError',
  'errorText',
  'hasMappingError',
  'matchFound',
  'doNotProcess',
  'isOnLeave',
  'toBeDeleted',
  'dietCount',
] as const satisfies readonly (keyof TblPatientPending)[];

/** A Message History list row. A full `TblPatientPending` is one too. */
export type EhrHistoryRow = Pick<TblPatientPending, (typeof EHR_HISTORY_ROW_FIELDS)[number]>;

/**
 * What the message viewer (EMRMessageDetail) and its mapping / match dialogs need.
 * `image` only comes with pending messages; history rows never carry it.
 */
export type EhrMessageRow = EhrHistoryRow & { image?: string | null };

/** Maps to C# TblUnit_Mapping */
export interface TblUnitMapping {
  pKey_Unit_Mapping: number;
  fKey_Facility: number;
  floor: string | null;
  wing: string | null;
  unit: string | null;
  b_Sort: number;
  l_Sort: number;
  d_Sort: number;
  fore_Color: number;
  back_Color: number;
  sM_B: string | null;
  sM_L: string | null;
  sM_D: string | null;
  useRoomTray: boolean;
}

// ===== HELPER FUNCTIONS =====

/** Derive the pending message status from the message flags */
export function getPendingStatus(msg: TblPatientPending): PendingStatus {
  if (msg.hasMappingError) return PENDING_STATUS.MAPPING_ERROR;
  if (!msg.matchFound) return PENDING_STATUS.NO_MATCH;
  return PENDING_STATUS.READY;
}

/** Derive the history message status */
export function getHistoryStatus(msg: Pick<TblPatientPending, 'processError'>): HistoryStatus {
  return msg.processError ? HISTORY_STATUS.ERROR : HISTORY_STATUS.SUCCESS;
}

/** Get the detail-panel status text (covers both pending and processed) */
export function getDetailStatusText(msg: TblPatientPending): string {
  if (msg.dateProcessed) return msg.processError ? 'Error' : 'Processed';
  if (msg.hasMappingError) return 'Mapping Error';
  if (!msg.matchFound) return 'No Match';
  return 'Ready';
}

/** Get the detail-panel status badge class */
export function getDetailStatusClass(msg: TblPatientPending): string {
  if (msg.dateProcessed) return msg.processError ? 'error' : 'processed';
  if (msg.hasMappingError) return 'error';
  if (!msg.matchFound) return 'no-match';
  return 'ready';
}

/** Gender code → display string */
export function getGenderDisplay(sex: string | null | undefined): string {
  switch (sex) {
    case 'M': return 'Male';
    case 'F': return 'Female';
    case 'U': return 'Unknown';
    default: return sex ?? 'N/A';
  }
}

/** Whether a pending message can be processed */
export function canProcess(msg: Pick<TblPatientPending, 'dateProcessed' | 'doNotProcess'>): boolean {
  if (msg.dateProcessed) return false;
  if (msg.doNotProcess) return false;
  return true;
}

/** Format a date string for display, returns 'N/A' if null */
export function formatEmrDate(dateStr: string | null | undefined, includeTime = false): string {
  if (!dateStr) return 'N/A';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 'N/A';
  const dateFormatted = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  if (!includeTime) return dateFormatted;
  const timeFormatted = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${dateFormatted} ${timeFormatted}`;
}

// ===== WARNING PRIORITY (4-state column in pending grid) =====

export const WARNING_PRIORITY = {
  MAPPING_ERROR: 'mapping-error',
  LOCKED: 'locked',
  NO_MATCH: 'no-match',
  WAITING_FOR_MAPPING: 'waiting-for-mapping',
  NONE: 'none',
} as const;
export type WarningPriority = (typeof WARNING_PRIORITY)[keyof typeof WARNING_PRIORITY];

export const WARNING_PRIORITY_CONFIG: Record<
  WarningPriority,
  { label: string; color: string; bgColor: string; icon: string; tooltip: string }
> = {
  [WARNING_PRIORITY.MAPPING_ERROR]:      { label: 'Mapping Error',      color: '#eab308', bgColor: '#fef9c3', icon: 'bi-exclamation-triangle-fill', tooltip: 'One or more diet/allergy items have no mapping' },
  [WARNING_PRIORITY.LOCKED]:             { label: 'Locked',             color: '#2563eb', bgColor: '#dbeafe', icon: 'bi-lock-fill',                  tooltip: 'Record is locked by another user' },
  [WARNING_PRIORITY.NO_MATCH]:           { label: 'No Match',           color: '#dc2626', bgColor: '#fee2e2', icon: 'bi-person-x-fill',              tooltip: 'No matching resident found - manual match required' },
  [WARNING_PRIORITY.WAITING_FOR_MAPPING]:{ label: 'Waiting',            color: '#dc2626', bgColor: '#fee2e2', icon: 'bi-clock-fill',                 tooltip: 'Earlier message for this resident has errors - resolve that first' },
  [WARNING_PRIORITY.NONE]:               { label: '',                   color: '#166534', bgColor: '#dcfce7', icon: 'bi-check-circle-fill',          tooltip: 'Ready to process' },
};

/**
 * Compute the 4-state warning priority for a pending message.
 * Priority order + rules ported verbatim from WinForms
 * `gvPending_CustomDrawCell` (frmDietech_Ehr_Pending_DataGrid.
 * vb:104-112):
 *   1. HasMappingError → warning
 *   2. FKey_User_Lock IS NOT NULL → lock (ANY user, incl. self)
 *   3. MatchFound = 0 → question
 *   4. (WaitingForMapping is handled by the caller — priority
 *      falls to WAITING regardless when the cascade set flags
 *      the row.)
 * Note: WinForms does NOT hide the lock icon for the current
 * user's own locks — earlier React logic gated on `!== userId`,
 * which meant a user who opened a message never saw the lock
 * indicator on their own held row.
 */
export function getWarningPriority(msg: TblPatientPending): WarningPriority {
  if (msg.hasMappingError) return WARNING_PRIORITY.MAPPING_ERROR;
  if (msg.fKey_User_Lock != null) return WARNING_PRIORITY.LOCKED;
  if (!msg.matchFound) return WARNING_PRIORITY.NO_MATCH;
  return WARNING_PRIORITY.NONE;
}

/**
 * Compute WaitingForMapping cascade — verbatim port of WinForms
 * `LoadData` cascade block (frmDietech_Ehr_Pending_DataGrid.vb:
 * 345-360).
 *
 * Semantics: iterate every pending row in DateEntered ASC order.
 * For each row that ISN'T already waiting AND has either
 * `hasMappingError` OR `!matchFound`, mark EVERY LATER row with
 * the same `patientID` as waiting. Later rows can't fire their
 * own cascade — once marked waiting they're treated as if their
 * hasMappingError/matchFound were cleared (WinForms literally
 * writes those flags off; React just uses set membership as the
 * suppression signal).
 *
 * Grouping key is `patientID` (raw incoming EMR identifier),
 * NOT `linked_ID` — pending messages exist BEFORE they've been
 * matched to a resident, so linked_ID may be null and cascade
 * would silently skip them. Earlier React logic keyed on
 * linked_ID and also only checked the earliest row per group;
 * both were wrong — later-arriving problems for unlinked
 * messages didn't cascade at all.
 */
export function computeWaitingForMapping(messages: TblPatientPending[]): Set<number> {
  const waitingIds = new Set<number>();

  // Bucket by patientID FIRST so the cascade is O(g·log g) per
  // group instead of the previous O(n²) full-scan-per-row. On a
  // facility with a few thousand pending rows the naive version
  // burned 25M+ compares per re-fetch and made the whole grid
  // feel laggy after any state update.
  const groups = new Map<string, Array<{ pKey: number; t: number; err: boolean }>>();
  for (const m of messages) {
    const key = m.patientID ?? '';
    if (!key) continue;
    const t = m.dateEntered ? new Date(m.dateEntered).getTime() : 0;
    const err = m.hasMappingError || !m.matchFound;
    const g = groups.get(key) ?? [];
    g.push({ pKey: m.pKey_Patient_Pending, t, err });
    groups.set(key, g);
  }

  for (const group of groups.values()) {
    if (group.length <= 1) continue;
    group.sort((a, b) => a.t - b.t);
    // Walk in DateEntered order; once we find a row not already
    // waiting that has err=true, mark every later row in the
    // group as waiting. Later problematic rows can't fire their
    // own cascade — matches WinForms `Not CBool(row("Waiting…"))`
    // guard (see .vb:351). Break after the cascade because every
    // subsequent row would either already be in waiting or be
    // suppressed by the guard.
    for (let i = 0; i < group.length; i++) {
      const row = group[i];
      if (!row.err) continue;
      for (let j = i + 1; j < group.length; j++) {
        waitingIds.add(group[j].pKey);
      }
      break;
    }
  }

  return waitingIds;
}

// ===== HISTORY DURATION FILTERS =====

export const HISTORY_DURATION = {
  TWENTY_FOUR_HOURS: '24-hours',
  TWO_WEEKS: '2-weeks',
  ONE_MONTH: '1-month',
  ALL: 'all',
} as const;
export type HistoryDuration = (typeof HISTORY_DURATION)[keyof typeof HISTORY_DURATION];

export function getHistoryDurationDays(duration: HistoryDuration): number | null {
  switch (duration) {
    case HISTORY_DURATION.TWENTY_FOUR_HOURS: return 1;
    case HISTORY_DURATION.TWO_WEEKS: return 14;
    case HISTORY_DURATION.ONE_MONTH: return 30;
    case HISTORY_DURATION.ALL: return null;
  }
}

// ===== RESIDENT MATCH =====

export const MATCH_FILTER_MODE = {
  NAME_BIRTHDATE: 'name-birthdate',
  LAST_NAME: 'last-name',
  BIRTHDATE: 'birthdate',
  ROOM: 'room',
} as const;
export type MatchFilterMode = (typeof MATCH_FILTER_MODE)[keyof typeof MATCH_FILTER_MODE];

export const MATCH_ACTION = {
  ADD_NEW: 'add-new',
  CHOOSE_EXISTING: 'choose-existing',
} as const;
export type MatchAction = (typeof MATCH_ACTION)[keyof typeof MATCH_ACTION];

export interface ResidentMatchResult {
  cancelled: boolean;
  action: MatchAction;
  selectedPatientId: number | null;
}

// ===== DIET ORDER MAPPING =====

export const MAPPING_TYPE = {
  DIET_TYPE: 'DietType',
  CONSISTENCY: 'Consistency',
  SPECIAL_NEEDS: 'SpecialNeeds',
  MEAL_PATTERN_NOTE: 'MealPatternNote',
  FEEDING_STATUS: 'FeedingStatus',
  SPECIAL_DEVICES: 'SpecialDevices',
} as const;
export type MappingType = (typeof MAPPING_TYPE)[keyof typeof MAPPING_TYPE];

export const MAPPING_STATUS = {
  MATCHED: 'matched',
  UNMATCHED: 'unmatched',
  DO_NOT_PROCESS: 'do-not-process',
  SKIPPED: 'skipped',
} as const;
export type MappingStatusType = (typeof MAPPING_STATUS)[keyof typeof MAPPING_STATUS];

export const MAPPING_STATUS_CONFIG: Record<
  MappingStatusType,
  { label: string; color: string; bgColor: string; icon: string }
> = {
  [MAPPING_STATUS.MATCHED]:        { label: 'Matched',        color: '#166534', bgColor: '#dcfce7', icon: 'bi-check-circle-fill' },
  [MAPPING_STATUS.UNMATCHED]:      { label: 'Unmatched',      color: '#92400e', bgColor: '#fef3c7', icon: 'bi-question-circle' },
  [MAPPING_STATUS.DO_NOT_PROCESS]: { label: 'Do Not Process', color: '#64748b', bgColor: '#f1f5f9', icon: 'bi-slash-circle' },
  [MAPPING_STATUS.SKIPPED]:        { label: 'Skipped',        color: '#64748b', bgColor: '#f1f5f9', icon: 'bi-skip-forward' },
};

/** Parse the diet order combined text into individual items */
export function parseDietOrderCombined(text: string | null): string[] {
  if (!text) return [];
  // WinForms splits on pipe, comma, or semicolon depending on config
  return text
    .split(/[|;]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Parse allergy combined text into individual items */
export function parseAllergyCombined(text: string | null): string[] {
  if (!text) return [];
  return text
    .split(/[|;,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * GET api/EMR/Clock/{facility} (EhrClock.cs). Offsets are minutes from UTC at
 * `utcNow` (Eastern daylight = -240). The facility zone is tblFacility; the
 * EHR zone is Connect's tblConnection_Customer.TimeZone, which the EHR worker
 * stamps processed times with.
 */
export interface EhrClockInfo {
  utcNow: string;
  facilityZoneId: string | null;
  facilityZoneValid: boolean;
  facilityUseDst: boolean;
  facilityZoneHasDst: boolean;
  facilityOffsetMinutes: number | null;
  /** False when Connect couldn't be read; the EHR zone fields are then empty. */
  connectReadable: boolean;
  connectMatched: boolean;
  ehrZoneId: string | null;
  ehrZoneValid: boolean;
  ehrOffsetMinutes: number | null;
  processing: 'worker' | 'desktop' | null;
  intakeZoneId: string;
  intakeOffsetMinutes: number | null;
  /** The zone the worker actually used, measured from recent rows. */
  observedProcessedOffsetMinutes: number | null;
  observedSample: number;
}
